import { Button } from '@heroui/react';
import type {
  LocationPrecision,
  PropertyZestimate,
} from '@attik/types/property.types';
import { Company, EmployeeWhenPopulated, Inspector, User } from './serverTypes';
import {
  MembershipType,
  MembershipWithCompanyType,
} from './serverTypeCollection/membership';

export type SessionType = {
  user: User &
    MembershipType & {
      employee: EmployeeWhenPopulated;
      company: Pick<
        Company,
        | '_id'
        | 'name'
        | 'address'
        | 'billingAddress'
        | 'timezone'
        | 'featureFlags'
        | 'subscriptions'
        | '_finixIdentityId'
        | '_finixSubscriptionId'
        | 'email'
        | 'phone'
        | 'logo'
        | 'url'
        | 'color'
        | 'colorSecondary'
        | 'attikStandalone'
        | 'attikNativeReportsEnabled'
        | 'everhomeEligible'
        | 'everhomeActive'
      >;
      subCompanies: Pick<
        Company,
        '_id' | 'name' | 'address' | 'billingAddress' | 'timezone'
      >[];
      integrations?: string[];
      memberships: MembershipWithCompanyType[];
      membership: MembershipWithCompanyType;
      attikStandalone: boolean;
    };
  sessionToken: string;
  expires?: string;
  userId: string;
};

export type PlaybookChapter = {
  id: number;
  attributes: {
    title: string;
    order: number;
    content: string;
    slug: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  };
};

export type s3Metadata = {
  format: string;
  key: string;
  url: string;
  id: string;
  extension: string;
  name: string;
  size?: string;
  width?: string;
  height?: string;
  preview?: string;
};

export type s3ObjectMetadata = {
  $metadata: {
    httpStatusCode: number;
    requestId: string;
    extendedRequestId: string;
    attempts: number;
    totalRetryDelay: number;
  };
  AcceptRanges: string;
  LastModified: string;
  ContentLength: number;
  ETag: string;
  ContentType: string;
  ServerSideEncryption: string;
  Metadata: s3Metadata;
};

export type AddressForScheduler = {
  address: string;
  street: string;
  unit?: string | null;
  city: string;
  state: string;
  zip: string;
  county?: string | null;
  lat: number;
  lng: number;
  /** How exactly `lat`/`lng` pins the building; absent when unknown. */
  locationPrecision?: LocationPrecision | null;
  sqft?: number;
  yearBuilt?: number;
  unitCount?: number;
  foundation?: string | null;
  /**
   * Zestimate readings carried from the property lookup through to save. Read
   * the current figure with `latestPropertyZestimate`; no `Verified` flag
   * because it is a provider figure, not a human-corrected one.
   */
  zestimates?: PropertyZestimate[];
  sqftVerified?: boolean;
  yearBuiltVerified?: boolean;
  unitCountVerified?: boolean;
  foundationVerified?: boolean;
};

// Foundation type options
export const FOUNDATION_TYPES = [
  'basement',
  'crawlspace',
  'slab',
  'elevated',
  'condo-not-ground-floor',
  'unsure',
] as const;

export type FoundationType = (typeof FOUNDATION_TYPES)[number];

// Foundation options for UI dropdowns
export const FOUNDATION_OPTIONS = [
  { value: 'basement', label: 'Basement' },
  { value: 'crawlspace', label: 'Crawlspace' },
  { value: 'slab', label: 'Slab' },
  { value: 'elevated', label: 'Elevated' },
  { value: 'condo-not-ground-floor', label: 'Condo - Not Ground Floor' },
  { value: 'unsure', label: 'Unsure' },
] as const;

// Helper to get display label from value
export const getFoundationLabel = (
  value: string | undefined | null
): string => {
  if (!value) return 'Not specified';
  const option = FOUNDATION_OPTIONS.find((opt) => opt.value === value);
  return option?.label || value;
};

export type PressEvent = Parameters<
  NonNullable<React.ComponentProps<typeof Button>['onPress']>
>[0];

export type CompanyPeriod = {
  length: number;
  start: number;
};

export type MasterDispatchData = {
  events: any[];
  inspector: Inspector;
  directions: any;
  color: string;
  inArea: boolean;
  calData: { calData: any[]; radonEnds?: any[] };
};
